"use client";

import { useEffect, useId, useRef } from "react";
import Link from "next/link";
import { useTrackEvent } from "@/hooks/useTrackEvent";
import {
  MEDIA_CONSENT_PURPOSE_LABELS,
  MEDIA_CONSENT_PURPOSES,
  type CheckoutConsentInput,
  type MediaConsentPurpose,
} from "@/lib/consent/bookingConsent";

type CheckoutConsentStepProps = {
  value: CheckoutConsentInput;
  onChange: (value: CheckoutConsentInput) => void;
  validationError?: string | null;
  requiresElyEarlyPerformanceRequest?: boolean;
};

export function CheckoutConsentStep({
  value,
  onChange,
  validationError,
  requiresElyEarlyPerformanceRequest = false,
}: CheckoutConsentStepProps) {
  const trackEvent = useTrackEvent({ includeAttribution: false });
  const termsId = useId();
  const termsDescriptionId = useId();
  const earlyPerformanceRequestId = useId();
  const earlyPerformanceAcknowledgementId = useId();
  const errorId = useId();
  const errorRef = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    trackEvent("terms_section_viewed");
    trackEvent("media_consent_section_viewed");
  }, [trackEvent]);

  useEffect(() => {
    if (validationError) errorRef.current?.focus();
  }, [validationError]);

  function update(patch: Partial<CheckoutConsentInput>) {
    onChange({ ...value, ...patch });
  }

  function setMediaOptIn(checked: boolean) {
    update(checked ? { mediaOptIn: true } : {
      mediaOptIn: false,
      mediaPurposes: [],
      reviewBeforeFirstPublication: false,
      mediaSubjectAgeBand: null,
    });
  }

  function setPurpose(purpose: MediaConsentPurpose, checked: boolean) {
    const nextPurposes = checked
      ? Array.from(new Set([...value.mediaPurposes, purpose]))
      : value.mediaPurposes.filter((candidate) => candidate !== purpose);
    update({ mediaPurposes: nextPurposes });
  }

  return (
    <div data-clarity-mask="true" className="space-y-4">
      <section aria-labelledby="terms-acceptance-title" className="rounded-[18px] border border-[#b7892d]/55 bg-[#fff5df] p-4 sm:p-5">
        <p className="text-[0.68rem] font-black uppercase tracking-[0.2em] text-[#8b621d]">Required before payment</p>
        <h3 id="terms-acceptance-title" className="mt-2 font-serif text-xl font-semibold text-[#123b34]">Terms and privacy</h3>
        <label htmlFor={termsId} className="mt-3 flex min-h-12 cursor-pointer items-start gap-3 rounded-[14px] border border-[#d8cab6] bg-white p-3 text-sm font-semibold leading-6 text-[#334640]">
          <input
            id={termsId}
            type="checkbox"
            checked={value.termsAccepted}
            onChange={(event) => update({ termsAccepted: event.target.checked })}
            required
            aria-required="true"
            aria-invalid={validationError === "Please accept the Terms and Conditions to continue."}
            aria-describedby={`${termsDescriptionId}${validationError ? ` ${errorId}` : ""}`}
            className="mt-1 h-5 w-5 shrink-0 accent-[#123b34]"
          />
          <span>I agree to the Singing Attitude Terms and Conditions and acknowledge the Privacy Notice.</span>
        </label>
        <p id={termsDescriptionId} className="mt-3 text-xs leading-5 text-[#61706b]">
          Read the{" "}
          <Link href="/terms" target="_blank" rel="noreferrer" onClick={() => trackEvent("terms_link_opened")} className="font-bold text-[#123b34] underline decoration-[#b7892d] underline-offset-4">Terms and Conditions</Link>
          {" "}and{" "}
          <Link href="/privacy" target="_blank" rel="noreferrer" onClick={() => trackEvent("privacy_link_opened")} className="font-bold text-[#123b34] underline decoration-[#b7892d] underline-offset-4">Privacy Notice</Link>
          {" "}in a new tab.
        </p>
      </section>

      {requiresElyEarlyPerformanceRequest ? (
        <section aria-labelledby="ely-early-performance-title" className="rounded-[18px] border border-[#b7892d]/55 bg-[#fff5df] p-4 sm:p-5">
          <p className="text-[0.68rem] font-black uppercase tracking-[0.2em] text-[#8b621d]">Separate express request · Ely</p>
          <h3 id="ely-early-performance-title" className="mt-2 font-serif text-xl font-semibold text-[#123b34]">Lesson within the 14-day cancellation period</h3>
          <p className="mt-2 text-sm leading-6 text-[#53615c]">
            These choices are separate from accepting the Terms. They apply only if your finally confirmed lesson is due to begin during the 14-day cancellation period, which starts the day after final confirmation.
          </p>
          <div className="mt-4 space-y-3">
            <label htmlFor={earlyPerformanceRequestId} className="flex min-h-12 cursor-pointer items-start gap-3 rounded-[14px] border border-[#d8cab6] bg-white p-3 text-sm font-semibold leading-6 text-[#334640]">
              <input
                id={earlyPerformanceRequestId}
                type="checkbox"
                checked={value.earlyPerformanceRequested}
                onChange={(event) => update({ earlyPerformanceRequested: event.target.checked })}
                required
                aria-required="true"
                className="mt-1 h-5 w-5 shrink-0 accent-[#123b34]"
              />
              <span>If my finally confirmed lesson is due to begin during the 14-day cancellation period, I expressly request that Singing Attitude begin providing the lesson service during that period.</span>
            </label>
            <label htmlFor={earlyPerformanceAcknowledgementId} className="flex min-h-12 cursor-pointer items-start gap-3 rounded-[14px] border border-[#d8cab6] bg-white p-3 text-sm font-semibold leading-6 text-[#334640]">
              <input
                id={earlyPerformanceAcknowledgementId}
                type="checkbox"
                checked={value.earlyPerformanceAcknowledged}
                onChange={(event) => update({ earlyPerformanceAcknowledged: event.target.checked })}
                required
                aria-required="true"
                className="mt-1 h-5 w-5 shrink-0 accent-[#123b34]"
              />
              <span>I understand that, where the law permits, cancelling after the requested service has begun may require payment proportionate to the service actually supplied up to cancellation. My Consumer Rights Act remedies are unaffected.</span>
            </label>
          </div>
        </section>
      ) : null}

      <section aria-labelledby="media-preference-title" className="rounded-[18px] border border-[#d8cab6] bg-white p-4 sm:p-5">
        <div>
          <p className="text-[0.68rem] font-black uppercase tracking-[0.2em] text-[#61706b]">Optional</p>
          <h3 id="media-preference-title" className="mt-2 font-serif text-xl font-semibold text-[#123b34]">Photography and video preference</h3>
          <p className="mt-2 text-sm leading-6 text-[#53615c]">This is separate from your purchase and starts unchecked.</p>
        </div>

        <div className="mt-4 space-y-4 border-t border-[#e8dccb] pt-4">
            <p className="text-sm leading-6 text-[#53615c]">From time to time, Singing Attitude may invite students to appear in photographs or recordings from lessons or programme activities for use in our website, social media, advertising and educational content. Giving consent is optional and will not affect your lessons or programme.</p>
            <label className="flex min-h-12 cursor-pointer items-start gap-3 rounded-[14px] border border-[#d8cab6] bg-[#fffdf8] p-3 text-sm font-semibold leading-6 text-[#334640]">
              <input type="checkbox" checked={value.mediaOptIn} onChange={(event) => setMediaOptIn(event.target.checked)} className="mt-1 h-5 w-5 shrink-0 accent-[#123b34]" />
              <span>Yes, I consent to Singing Attitude photographing or recording me and using approved images or clips for digital content.</span>
            </label>

            {value.mediaOptIn ? (
              <div className="space-y-4 rounded-[14px] border border-[#d8cab6] bg-[#fffaf2] p-4">
                <fieldset>
                  <legend className="text-sm font-bold text-[#123b34]">Is the person who may be recorded 18 or over?</legend>
                  <div className="mt-2 grid gap-2 sm:grid-cols-2">
                    <label className="flex min-h-11 cursor-pointer items-center gap-2 rounded-[12px] border border-[#d8cab6] bg-white px-3 text-sm font-semibold text-[#334640]"><input type="radio" name="media-age-band" checked={value.mediaSubjectAgeBand === "adult"} onChange={() => update({ mediaSubjectAgeBand: "adult" })} />Yes, 18 or over</label>
                    <label className="flex min-h-11 cursor-pointer items-center gap-2 rounded-[12px] border border-[#d8cab6] bg-white px-3 text-sm font-semibold text-[#334640]"><input type="radio" name="media-age-band" checked={value.mediaSubjectAgeBand === "under_18"} onChange={() => update({ mediaSubjectAgeBand: "under_18", mediaPurposes: [], reviewBeforeFirstPublication: false })} />No, under 18</label>
                  </div>
                </fieldset>

                {value.mediaSubjectAgeBand === "under_18" ? (
                  <div role="status" className="rounded-[12px] border border-amber-300 bg-amber-50 p-3 text-sm leading-6 text-amber-950">
                    <p className="font-bold">Guardian approval required</p>
                    <p>Online media consent is not activated for an under-18 student. Purchase can continue, but a separate verified guardian process and the young person&apos;s agreement are required before any recording.</p>
                  </div>
                ) : value.mediaSubjectAgeBand === "adult" ? (
                  <fieldset>
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <legend className="text-sm font-bold text-[#123b34]">Choose each permitted use</legend>
                      <div className="flex gap-2">
                        <button type="button" onClick={() => update({ mediaPurposes: [...MEDIA_CONSENT_PURPOSES] })} className="min-h-10 rounded-full border border-[#d8cab6] px-3 text-xs font-bold text-[#123b34]">Select all</button>
                        <button type="button" onClick={() => update({ mediaPurposes: [], reviewBeforeFirstPublication: false })} className="min-h-10 rounded-full border border-[#d8cab6] px-3 text-xs font-bold text-[#123b34]">Clear all</button>
                      </div>
                    </div>
                    <div className="mt-3 grid gap-2 sm:grid-cols-2">
                      {MEDIA_CONSENT_PURPOSES.map((purpose) => (
                        <label key={purpose} className="flex min-h-11 cursor-pointer items-start gap-2 rounded-[12px] border border-[#d8cab6] bg-white p-3 text-sm leading-5 text-[#334640]">
                          <input type="checkbox" checked={value.mediaPurposes.includes(purpose)} onChange={(event) => setPurpose(purpose, event.target.checked)} className="mt-0.5 h-4 w-4 shrink-0 accent-[#123b34]" />
                          <span>{MEDIA_CONSENT_PURPOSE_LABELS[purpose]}</span>
                        </label>
                      ))}
                    </div>
                    <label className="mt-3 flex min-h-11 cursor-pointer items-start gap-2 rounded-[12px] border border-[#d8cab6] bg-white p-3 text-sm leading-5 text-[#334640]">
                      <input type="checkbox" checked={value.reviewBeforeFirstPublication} onChange={(event) => update({ reviewBeforeFirstPublication: event.target.checked })} className="mt-0.5 h-4 w-4 shrink-0 accent-[#123b34]" />
                      <span><span className="font-semibold">I would like to review identifiable images or clips before their first publication.</span><span className="mt-1 block text-xs text-[#61706b]">Workflow preference only — not a contractual guarantee unless separately agreed.</span></span>
                    </label>
                  </fieldset>
                ) : null}
              </div>
            ) : null}

            <ul className="space-y-1 text-xs leading-5 text-[#61706b]">
              <li>Participation is optional and does not affect price, access or coaching quality.</li>
              <li>You can withdraw consent for future use and can decline any particular recording.</li>
              <li>No filming starts automatically because this box is selected.</li>
              <li>Specific recording arrangements are confirmed separately before a session.</li>
            </ul>
        </div>
      </section>

      {validationError ? (
        <p id={errorId} ref={errorRef} role="alert" tabIndex={-1} className="rounded-[14px] border border-rose-300 bg-rose-50 p-3 text-sm font-semibold text-rose-900 outline-none">
          {validationError}
        </p>
      ) : null}
    </div>
  );
}
